import { RefObject, useMemo } from 'react';
import { css } from '@emotion/react';
import { CallIntelTypes } from '@frontend/api-call-intel';
import { callIntelUtils, callStatusUtils, generateTrackingId, TrackingOptions } from '@frontend/call-intel-core';
import { PrivateCommsBanner, CallIntelPrivateCommsBannerSource } from '@frontend/high-value-adoption';
import { useTranslation } from '@frontend/i18n';
import { breakpoints } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import {
  DownloadIcon,
  EmptyStateConfig,
  IconButton,
  ListsIcon,
  Table,
  TableColumnConfig,
} from '@frontend/design-system';
import { trackingIds } from '../../../tracking-ids';

type CallRecordsTableViewProps = {
  // Data and query props
  calls: CallIntelTypes.CallRecord[];
  isLoading: boolean;
  emptyStateConfig: EmptyStateConfig<CallIntelTypes.CallRecord>;

  // Pagination and table state
  tableState: {
    pageConfig: { pageNumber: number; pageSize: number };
    sortType: CallIntelTypes.SortType;
  };
  hasNextPage: boolean;
  hasPreviousPage: boolean;

  // Table columns from parent
  tableColumns: TableColumnConfig<CallIntelTypes.CallRecord>[];

  // Data configuration
  dataConfig: {
    officeNumbers?: string[];
    editedCallIds: string[];
    minSearchLength: number;
    isDemoAccount?: boolean;
    hasPrivateCalls: boolean;
    privateCommBannerSource: CallIntelPrivateCommsBannerSource;
  };

  // Panel and UI configuration
  panelConfig: {
    selectedCall?: CallIntelTypes.CallRecord | null;
  };

  // Display configuration
  displayConfig: {
    customToolbar?: React.ReactNode;
    searchResultSummary?: React.ReactNode;
    hideFilters?: (keyof CallIntelTypes.Filters)[];
    marginTop: number;
    trayNotificationMessage?: React.ReactNode;
    drillDownFilters?: CallIntelTypes.DrillDownOptions;
    exportBtnRef?: RefObject<HTMLButtonElement>;
    isTranscriptSearchActive?: boolean;
    showExportAction?: boolean;
  };

  // Event handlers
  handlers: {
    onFiltersChange: () => void;
    onSearchChange: (value: string, debounced?: boolean) => void;
    onSearchReset?: () => void;
    onSorting: (sortValues: any[]) => void;
    onPagination: (action: 'next' | 'prev') => void;
    onPageSizeChange: (pageSize: number) => void;
    onExport: () => void;
    onRowActionClick: (rowData: CallIntelTypes.CallRecord) => void;
  };

  // Utility functions from parent
  getTrayIcon: (call?: CallIntelTypes.CallRecord, isDemoAccount?: boolean, isEdited?: boolean) => React.ReactNode;

  //Trackings for table
  trackingOptions?: Record<string, TrackingOptions>;
};

export const CallRecordsTableView = ({
  calls,
  isLoading,
  emptyStateConfig,
  tableState,
  hasNextPage,
  hasPreviousPage,
  tableColumns,
  dataConfig,
  panelConfig,
  displayConfig,
  handlers,
  getTrayIcon,
}: CallRecordsTableViewProps) => {
  const { t } = useTranslation('analytics');

  const { editedCallIds, isDemoAccount, hasPrivateCalls, privateCommBannerSource } = dataConfig;

  const { selectedCall } = panelConfig;

  const {
    customToolbar,
    searchResultSummary,
    marginTop,
    drillDownFilters,
    isTranscriptSearchActive,
    showExportAction,
  } = displayConfig;

  const { onSorting, onPagination, onPageSizeChange, onExport, onRowActionClick } = handlers;

  const hasExtraToolbarContent = !!searchResultSummary || hasPrivateCalls;

  const extraToolbarContent = useMemo(() => {
    if (!hasExtraToolbarContent) return undefined;
    return (
      <div css={styles.extraToolbarContent}>
        {searchResultSummary}
        {hasPrivateCalls && <PrivateCommsBanner source={privateCommBannerSource} />}
      </div>
    );
  }, [hasExtraToolbarContent, hasPrivateCalls, privateCommBannerSource, searchResultSummary]);

  return (
    <Table
      colConfig={tableColumns}
      customToolbarRender={() => customToolbar}
      extraToolbarContent={extraToolbarContent}
      data={calls}
      disableMultiSort
      emptyStateConfig={emptyStateConfig}
      fullHeight={!drillDownFilters}
      allowMobileView={false}
      globalTrackingId='call-intel-records'
      hasFilterColumns={!isTranscriptSearchActive}
      hasGlobalSearch={false}
      hasResizeColumns
      hasResponsiveColWidths
      isLoading={isLoading}
      isPaginated
      manualPaginationConfig={{
        handleChange: onPagination,
        hasNext: hasNextPage,
        hasPrevious: hasPreviousPage,
        page: tableState.pageConfig.pageNumber,
        defaultRowsPerPage: tableState.pageConfig.pageSize,
        onNumRowsChange: onPageSizeChange,
        rowsPerPageOptions: [10, 25, 50, 100],
      }}
      manualSortBy
      onSortChange={onSorting}
      rowActions={{
        actions: [
          {
            // Label and Icon both are required properties (as of now) even if renderCustom is provided
            // Hence, passing both of them to avoid typescript errors
            Icon: ListsIcon,
            label: t('Call Insights'),
            renderCustom: (_getItemProps, _index, _close, _isSingleAction, rowData) => {
              const {
                isFailedCall,
                isSkippedCall,
                isProcessingCall,
                isPoorAudio: statusIsPoorAudio,
              } = callStatusUtils.getCallFlags(rowData);

              const isPoorAudio = isDemoAccount ? false : statusIsPoorAudio;
              const isEdited = editedCallIds.includes(rowData.id ?? '');

              return (
                <IconButton
                  key={rowData.id}
                  label={
                    isEdited
                      ? t('Edits to this Call Insight have been saved.')
                      : isProcessingCall
                      ? t('We are analyzing this call.')
                      : isFailedCall
                      ? t('We are unable to transcribe and analyze this call.')
                      : isSkippedCall
                      ? t(
                          'This call was excluded from analysis because it had only one participant or the audio was too short or too long. Please review the transcript.'
                        )
                      : isPoorAudio
                      ? t(
                          'The transcript and details of this call may be inaccurate due to poor audio quality. Please review the recording.'
                        )
                      : t('Call Insights')
                  }
                  onClick={() => onRowActionClick(rowData)}
                  showLabelOnHover
                  data-testid='call-insight-action-button'
                  trackingId={trackingIds.callIntel.openCallTakeAwayTray}
                >
                  {getTrayIcon(rowData, isDemoAccount, isEdited)}
                </IconButton>
              );
            },
          },
        ],
      }}
      styleConfig={{
        columns: [
          {
            id: 'officeNumber',
            cellStyler: (_cellVal, _column, rowObject) => {
              const isDataUnavailable = callIntelUtils.isOfficeUserDataUnavailable(rowObject.original.startTime);

              return css`
                color: ${isDataUnavailable ? theme.colors.neutral50 : 'inherit'};
              `;
            },
          },
        ],
        rows: (rowData) => {
          return rowData.id === selectedCall?.id ? { background: theme.colors.neutral5 } : {};
        },
      }}
      tableInstanceId='uploaded-call-records'
      tableActions={[
        {
          Icon: DownloadIcon,
          hide: !showExportAction,
          label: t('Download Data'),
          trackingId: generateTrackingId({
            component: 'call-records-table',
            context: 'export-icon',
            isDemoAccount,
          }),
          onClick: onExport,
          type: 'icon',
          ref: displayConfig.exportBtnRef,
        },
      ]}
      wrapperStyle={styles.wrapperStyle({ marginTop, hasExtraToolbarContent })}
    />
  );
};

const styles = {
  filtersWrapper: css`
    order: -1;
    margin-right: ${theme.spacing(2)};

    @media screen and (max-width: 1500px) {
      flex: 1;
    }
  `,

  extraToolbarContent: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
  `,

  wrapperStyle: ({ marginTop, hasExtraToolbarContent }: { marginTop: number; hasExtraToolbarContent: boolean }) => css`
    margin-top: ${marginTop}px;
    .table-toolbar {
      align-items: flex-end;
      padding: ${theme.spacing(1, 0)};

      @container (max-width: ${breakpoints.xsmall.max}px) {
        flex-direction: column;
        gap: ${theme.spacing(1)};
        align-items: flex-end;
        margin-bottom: 0;
      }
    }
    ${hasExtraToolbarContent &&
    css`
      .table-toolbar {
        margin-bottom: ${theme.spacing(0)};
      }
      .table-toolbar-extra-content {
        margin-top: ${theme.spacing(1)};
        margin-bottom: ${theme.spacing(2)};
      }
    `}
  `,
};
